import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  AttributeFieldTypes,
  GenericProductAttributeSelector,
  ProductAttributeSelector,
  useProductAttributeFieldType,
  useProductDetailSelectionState,
  useErrorRegistrationContext,
  useId,
  useTheme,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { ProductDetailLabel } from '../ProductDetailLabels';

const DynamicFormattedMessage = FormattedMessage;

const Selector = styled(GenericProductAttributeSelector)`
  margin-top: 16px;
  margin-bottom: 32px;
  display: ${({ isHidden }) => (isHidden ? 'none' : 'block')};
`;

const itemSizeLabelStyle = css`
  ${({ theme }) => theme.selectorStyles.itemSizeLabelStyle};
  text-transform: none;
`;

const inputSizeStyle = css`
  ${({ theme }) => theme.selectorStyles.inputSizeStyle};
  width: 150px;
  height: 40px;
`;

const listStyle = css`
  margin: 0;
  gap: 8px;
`;

const listItemStyle = css`
  margin: 0;
  padding: 0;
`;

const SelectorWrapper = styled.div`
  font-size: 14px;
`;

const SelectOptionLabelWrapper = styled.span`
  display: flex;
  padding-top: 9px;
  margin: 6px 0;
`;

const SelectOptionLabelField = styled.span`
  color: ${({ theme }) => theme.colors.textDeemphasized};
  line-height: 1.14;
  letter-spacing: ${-0.25 / 14}em;
  font-weight: 600;
  margin-right: 2px;
`;

const SelectOptionLabelText = styled.span`
  color: ${({ theme }) => theme.colors.errorMessage};
  line-height: 1.14;
  letter-spacing: ${-0.25 / 14}em;
  text-transform: none;
  font-weight: 600;
`;

const SoldOutMessage = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.errorMessage};
  display: flex;
  font-weight: 600;
`;

const SizeLabelWrapper = styled.span`
  display: flex;
`;

const OptionSummary = ({ attribute, selectedOption, field }) => {
  const { productIndex } = useProductDetailSelectionState();
  const uniqueId = useId(`${productIndex}-${attribute.field}-`);
  const theme = useTheme();
  const errorRegistrationDisabled = Boolean(
    theme.context?.errorRegistrationDisabled
  );
  const ref = useRef();
  const hasError =
    (selectedOption && !selectedOption.isAvailable) || !selectedOption;
  useErrorRegistrationContext(
    ref,
    errorRegistrationDisabled ? false : hasError,
    uniqueId
  );

  const {
    selectorStyles: { selectedLabelTextStyle, selectorLabelStyle },
  } = useTheme();

  return (
    <SelectorWrapper ref={ref}>
      {!selectedOption && (
        <SelectOptionLabelWrapper>
          <SelectOptionLabelField>
            <DynamicFormattedMessage
              id={`site_product_page.${attribute.field}`}
              defaultMessage={attribute.field}
            />
            {': '}
          </SelectOptionLabelField>
          <SelectOptionLabelText>
            <FormattedMessage
              id="site_product_page.select_an_option"
              defaultMessage="Select an option"
            />
          </SelectOptionLabelText>
        </SelectOptionLabelWrapper>
      )}
      <SizeLabelWrapper>
        {selectedOption && (
          <ProductDetailLabel
            attribute={attribute}
            selectedOption={selectedOption}
            labelTextStyles={selectorLabelStyle}
            infoTextStyles={selectedLabelTextStyle}
            field={field}
          />
        )}{' '}
        {selectedOption &&
          (!selectedOption.isAvailable ||
            !selectedOption.isAvailableForUser) && (
            <SoldOutMessage data-autotag="pdp-size-sold-out">
              &nbsp;-&nbsp;
              <FormattedMessage
                id="site_product_page.out_of_stock"
                defaultMessage="Out of Stock"
              />
            </SoldOutMessage>
          )}
      </SizeLabelWrapper>
    </SelectorWrapper>
  );
};

OptionSummary.propTypes = {
  attribute: PropTypes.object,
  field: PropTypes.string,
  selectedOption: PropTypes.object,
};

function ProductDetailOtherSelectors(props) {
  const { disableAllOptions, ...restProps } = props;
  const otherAttributes = useProductAttributeFieldType(
    AttributeFieldTypes.OTHER
  );
  const { hiddenAttributes } = useProductDetailSelectionState();

  const {
    selectorStyles: { itemSoldOutStyle },
  } = useTheme();

  if (!otherAttributes) {
    return null;
  }

  return otherAttributes.map((fieldItem) => {
    const isSelectorHidden = hiddenAttributes.includes(fieldItem);
    return (
      <ProductAttributeSelector key={fieldItem} field={fieldItem}>
        {({ attribute, selectedOption }) => (
          <Selector
            inputStyle={inputSizeStyle}
            itemLabelStyle={itemSizeLabelStyle}
            itemStyle={listItemStyle}
            itemSoldOutStyle={itemSoldOutStyle}
            isHidden={isSelectorHidden}
            listStyle={listStyle}
            renderSummary={() => (
              <OptionSummary
                attribute={attribute}
                selectedOption={selectedOption}
                field={fieldItem}
              />
            )}
            radioButtonProps={{
              'data-autotag': `pdp-${attribute.field.toLowerCase()}-option`,
            }}
            disableAllOptions={disableAllOptions}
            {...restProps}
          />
        )}
      </ProductAttributeSelector>
    );
  });
}

ProductDetailOtherSelectors.propTypes = {
  disableAllOptions: PropTypes.bool,
};

export default ProductDetailOtherSelectors;
